import { Chart as ChartJs } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import chroma from 'chroma-js';
import IconButton from 'components/IconButton';
import history from 'history.js';
import { showWarningToast } from 'lib/ToastHelper';
import React, { useEffect, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardBody, CardTitle, Row, Col } from 'reactstrap';

import { getHistoryChartData } from './helper';
import {
  LINE_CHART_OPTIONS,
  LINE_CHART_OPTIONS_NO_COUNT_LABELS,
} from './options';
import { IntervalFilter, TooltipLabelsType } from '../../types';

import 'views/Admin/Dashboard/styles.css';

type Props = {
  title: string;
  subtitle?: string;
  labels: string[];
  tooltipLabels: TooltipLabelsType;
  datasets: number[][];
  colors: string[];
  showCounts: boolean;
  isActive?: boolean;
  selectedHostType?: string;
  selectedInterval?: IntervalFilter;
  lastSyncedDate?: string;
  currentVulnCount?: string;
  newDataGenerationIndex: number;
  lastIntervalDrillDownUrl?: string;
  getDrillDownUrl: (sourceType: string) => string;
  onExpandClick?: () => void;
  // This parameter will be used to show last interval days count in the chart.
  lastIntervalCount?: number;
  colorScale?: (value: number) => string; // New optional parameter
};

const HistoryChart = ({
  title,
  subtitle,
  labels,
  tooltipLabels,
  datasets,
  colors,
  showCounts,
  isActive = false,
  newDataGenerationIndex,
  selectedHostType,
  selectedInterval,
  lastSyncedDate,
  currentVulnCount,
  lastIntervalDrillDownUrl,
  getDrillDownUrl,
  onExpandClick,
  lastIntervalCount,
  colorScale, // Optional parameter
}: Props) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let chartReference = useRef<any>();
  const [redirectUrl, setRedirectUrl] = useState('');

  useEffect(() => {
    if (redirectUrl) history.push(redirectUrl);
  }, [redirectUrl]);

  let themeMode = localStorage.getItem('mode');

  useEffect(() => {
    const chart = chartReference?.current?.chartInstance;
    if (chart) {
      chart.options = showCounts
        ? LINE_CHART_OPTIONS
        : LINE_CHART_OPTIONS_NO_COUNT_LABELS;
      chart.update();
    }
  }, [showCounts, themeMode]);

  function getTotalCount() {
    let count = 0;
    if (datasets?.length) {
      datasets.forEach((dataset) => {
        if (dataset?.length) count += dataset[dataset?.length - 1];
      });
    }
    return count;
  }

  function showDrillDownToast() {
    showWarningToast('Drilldown only available when Interval is 1w.');
  }

  function handleChartClick(event, chart: ChartJs): void {
    // Chart.js v3.9.1 with react-chartjs-2 v3.0.4,
    if (!chart) return;
    const activeElements = chart.getElementsAtEventForMode(
      event,
      'nearest',
      { intersect: true },
      false,
    );
    // If not 'all' and not 1 week, show toast and do not drilldown
    if (selectedInterval !== IntervalFilter.Week && title !== 'All') {
      showDrillDownToast();
      return;
    }

    if (activeElements?.length > 0) {
      const activeElement = activeElements[0];
      const datasetIndex = activeElement.datasetIndex;

      const dataset = chart.data.datasets[datasetIndex];
      const sourceType = dataset.label;
      if (sourceType) {
        const url = getDrillDownUrl(sourceType);
        if (url) {
          setRedirectUrl(url);
        }
      }
    } else {
      // If no element is clicked, redirect to the default drill down URL
      const url = getDrillDownUrl('');
      if (url) {
        setRedirectUrl(url);
      }
    }
  }

  const totalsCount = getTotalCount().toString();
  const LineChart = (
    <div
      className="chart-area"
      style={{ cursor: 'pointer', height: '220px', width: '100%' }}
    >
      <Line
        ref={(node) => {
          if (node != null) chartReference.current = node;
        }}
        options={
          showCounts ? LINE_CHART_OPTIONS : LINE_CHART_OPTIONS_NO_COUNT_LABELS
        }
        data={getHistoryChartData(
          labels,
          tooltipLabels,
          datasets,
          colors,
          newDataGenerationIndex,
          colorScale, // Pass the colorScale parameter
        )}
        plugins={[ChartDataLabels]}
        onClick={(event) =>
          handleChartClick(event, chartReference.current as ChartJs)
        }
      />
    </div>
  );

  const handleSubtitleOrLegendsClick = (drillDownUrl: string) => {
    if (selectedInterval !== IntervalFilter.Week && title !== 'All') {
      showDrillDownToast();
      return;
    }
    if (drillDownUrl) {
      setRedirectUrl(drillDownUrl);
      return;
    }
  };

  const renderLegends = (): JSX.Element => {
    if (tooltipLabels?.length > 0) {
      let index = 0;
      return (
        <div className="mr-1">
          {tooltipLabels?.map((tooltip) => {
            let color = colors[index++];
            let bgColor = chroma(color).alpha(0.3);
            const drillDownUrl = getDrillDownUrl(tooltip.label);
            return (
              <div
                key={color}
                className="d-inline"
                onClick={() => {
                  handleSubtitleOrLegendsClick(drillDownUrl);
                }}
              >
                <div
                  className="m-1 p-1 legend-box"
                  style={{ borderColor: color, backgroundColor: bgColor }}
                />
                <span className="d-inline legend-text">
                  {tooltip.hostsCount !== null
                    ? `${tooltip.label} (${tooltip.hostsCount})`
                    : tooltip.label}
                </span>
              </div>
            );
          })}
        </div>
      );
    } else {
      return <></>;
    }
  };
  return (
    <Card className="card-chart">
      <CardHeader>
        <Row className="justify-content-between">
          <Col className="col-auto pr-0">
            <CardTitle tag="div" className="mb-0">
              {selectedInterval === IntervalFilter.Week ? (
                <Link
                  to={getDrillDownUrl(
                    selectedHostType ? selectedHostType : 'All',
                  )}
                >
                  <div className="d-flex flex-row align-items-baseline">
                    <h1 className="d-inline mb-0">{totalsCount}</h1>
                    <h5 className="card-category px-1">{title}</h5>
                    <h5
                      className="card-category px-1"
                      style={{ textTransform: 'none' }}
                    >
                      {totalsCount !== currentVulnCount &&
                      currentVulnCount !== undefined &&
                      lastSyncedDate !== undefined
                        ? '(Last Synchronized at: ' + lastSyncedDate + ')'
                        : ''}
                    </h5>
                  </div>
                </Link>
              ) : (
                <div className="d-flex flex-row align-items-baseline">
                  <h1 className="d-inline mb-0">{totalsCount}</h1>
                  <h5 className="card-category px-1">{title}</h5>
                  <h5
                    className="card-category px-1"
                    style={{ textTransform: 'none' }}
                  >
                    {totalsCount !== currentVulnCount &&
                    currentVulnCount !== undefined &&
                    lastSyncedDate !== undefined
                      ? '(Last Synchronized at: ' + lastSyncedDate + ')'
                      : ''}
                  </h5>
                </div>
              )}
            </CardTitle>
          </Col>
          <Col className="col p-0">
            <div className="col-12 d-flex flex-row-reverse">
              {onExpandClick && (
                <IconButton
                  id={'expand' + title.substring(0, 2)}
                  iconClass={
                    isActive
                      ? 'bi bi-arrows-angle-contract'
                      : 'bi bi-arrows-angle-expand'
                  }
                  tooltip={isActive ? 'Contract' : 'Expand'}
                  size="sm"
                  cssClass="padding-2"
                  active={isActive}
                  onClick={() => {
                    if (onExpandClick) {
                      onExpandClick();
                    }
                  }}
                />
              )}
            </div>
          </Col>
        </Row>
        <Row className="justify-content-between">
          <Col className="col-auto pr-0">
            <CardTitle tag="div" className="mb-0">
              {subtitle && lastIntervalDrillDownUrl && (
                <div
                  className="d-flex flex-row align-items-baseline"
                  onClick={() => {
                    handleSubtitleOrLegendsClick(lastIntervalDrillDownUrl);
                  }}
                >
                  <h4 className="px-1 font-weight-600">{lastIntervalCount}</h4>
                  <p>{subtitle}</p>
                </div>
              )}
            </CardTitle>
          </Col>
          <Col className="col p-0">
            <div className="col-12 d-flex flex-row-reverse">
              {renderLegends()}
            </div>
          </Col>
        </Row>
      </CardHeader>
      <CardBody>
        <div
          className="chart-area"
          style={{ cursor: 'pointer', height: '220px', width: '100%' }}
        >
          {LineChart}
        </div>
      </CardBody>
    </Card>
  );
};

export default HistoryChart;
